import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { adminApiError } from '../adminApi';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import { LabelItemView, ProgressBar } from '../evals/LabelItemView';
import {
  calibrationErrorText,
  itemHref,
  itemNeighbours,
  markLabelled,
  nextUnlabelled,
  reportHref,
  setHref,
  suiteKindText,
  type CalibrationItemForLabel,
  type CalibrationItemSummary,
  type CalibrationLabelSaved,
} from '../evals/labels';
import { labelsApi } from '../evals/labelsApi';
import { GuideLink } from '../evals/GuideLink';
import '../../styles/admin-eval-labels.css';

// Blind labelling, one item at a time (TOM-141; ADR 0234 §3). The page reads
// the item's frozen case and reply; the API answers its verdict null until this
// admin has labelled it, and the page shows a verdict only through
// verdictToShow, after the label is saved. A held-out test-split item (ADR
// 0238) is not read at all until the admin reveals it: the reveal is the read,
// with revealTest=true, and the server audits it before answering. Saving a
// test item's label returns its verdict, so the save carries the reveal too.
// Nothing here links an item to its result or run.

function TestSplitGate({ caseId, onReveal }: { caseId: string; onReveal: () => void }) {
  return (
    <div className="eval-hidden-panel" data-testid="eval-label-test-gate">
      <p>
        <EvalBadge tone="warn">Test split</EvalBadge> <code>{caseId}</code> is held out (ADR 0238).
        Its message, rubric, facts and reply stay hidden until you reveal them, so nobody tunes the
        assistant to it. The reveal is audited: your name, the item and its case are logged before
        anything is shown.
      </p>
      <button
        type="button"
        className="admin-button-secondary"
        onClick={onReveal}
        data-testid="eval-label-reveal"
      >
        Reveal this item
      </button>
    </div>
  );
}

/**
 * One item's reveal, read and save. Keyed by the item, so moving to another
 * item starts with nothing revealed, read or saved: a reveal never carries
 * over to the next test item.
 */
function ItemPane({
  setId,
  summary,
  onSaved,
  next,
}: {
  setId: string;
  summary: CalibrationItemSummary;
  onSaved: (itemId: string) => void;
  next: ReactNode | undefined;
}) {
  const { itemId, testSplit } = summary;
  const [revealed, setRevealed] = useState(false);
  const [item, setItem] = useState<CalibrationItemForLabel | null>(null);
  const [saved, setSaved] = useState<CalibrationLabelSaved | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mayRead = !testSplit || revealed;

  useEffect(() => {
    if (!mayRead) return;
    let ignore = false;
    labelsApi
      .item(setId, itemId, testSplit)
      .then((it) => {
        if (!ignore) setItem(it);
      })
      .catch((err) => {
        const body = adminApiError(err);
        if (!ignore) setError(calibrationErrorText(body.error, body.message));
      });
    return () => {
      ignore = true;
    };
  }, [mayRead, setId, itemId, testSplit]);

  async function save(label: boolean, note: string | null) {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await labelsApi.saveLabel(setId, itemId, { label, note }, testSplit);
      setSaved(result);
      onSaved(itemId);
    } catch (err) {
      const body = adminApiError(err);
      setSaveError(calibrationErrorText(body.error, body.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <EvalError message={error} />
      {testSplit && !revealed ? (
        <TestSplitGate caseId={summary.caseId} onReveal={() => setRevealed(true)} />
      ) : null}
      {testSplit && item !== null ? (
        <p className="eval-callout" data-testid="eval-label-revealed">
          Revealed. This access is logged.
        </p>
      ) : null}
      {mayRead && item === null && error === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : null}
      {item !== null ? (
        <LabelItemView
          item={item}
          saved={saved}
          saving={saving}
          error={saveError}
          onSave={(label, note) => void save(label, note)}
          next={next}
        />
      ) : null}
    </>
  );
}

export function AdminEvalLabelItem() {
  const { setId = '', itemId = '' } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState<CalibrationItemSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The set's items: the order, this admin's progress, and which are test split.
  useEffect(() => {
    let ignore = false;
    setItems(null);
    setError(null);
    labelsApi
      .items(setId)
      .then((list) => {
        if (!ignore) setItems(list);
      })
      .catch((err) => {
        const body = adminApiError(err);
        if (!ignore) setError(calibrationErrorText(body.error, body.message));
      });
    return () => {
      ignore = true;
    };
  }, [setId]);

  const summary = items?.find((i) => i.itemId === itemId) ?? null;
  const nav = items !== null ? itemNeighbours(items, itemId) : null;
  const upNext = items !== null ? nextUnlabelled(items, itemId) : null;
  const labelled = items?.filter((i) => i.labelledByYou).length ?? 0;
  // Labelled by this admin, before or just now: the next step is the next item.
  const done = summary?.labelledByYou === true;
  const next =
    upNext !== null ? (
      <button
        type="button"
        className="admin-button-affirm"
        onClick={() => navigate(itemHref(setId, upNext.itemId))}
        data-testid="eval-label-next-unlabelled"
      >
        Next unlabelled item →
      </button>
    ) : (
      <Link
        className="admin-button-affirm eval-label-cta"
        to={reportHref(setId)}
        data-testid="eval-label-to-report"
      >
        Every item labelled: read the report →
      </Link>
    );

  return (
    <section className="admin-section eval-page">
      <p className="admin-hint">
        <Link to={setHref(setId)}>← The set</Link>
      </p>
      <header className="admin-section-header">
        <h1>
          {nav !== null && nav.index >= 0 && items !== null
            ? `Item ${nav.index + 1} of ${items.length}`
            : 'Item'}
        </h1>
        {items !== null ? (
          <span className="admin-count">
            <ProgressBar labelled={labelled} total={items.length} />
          </span>
        ) : null}
        <GuideLink section="labels" />
      </header>

      <EvalError message={error} />
      {items === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}
      {items !== null && summary === null ? (
        <EvalError message="This item is not in the set." />
      ) : null}

      {summary !== null ? (
        <>
          <p className="admin-muted eval-sub" data-testid="eval-label-item-meta">
            <code>{summary.caseId}</code> · {suiteKindText(summary.suite, summary.kindOrSurface)}
            {summary.split !== null ? ` · ${summary.split} split` : ''}
          </p>
          <ItemPane
            key={`${setId}/${itemId}`}
            setId={setId}
            summary={summary}
            onSaved={(id) => setItems((list) => (list === null ? list : markLabelled(list, id)))}
            next={done ? next : undefined}
          />
        </>
      ) : null}

      {nav !== null && nav.index >= 0 ? (
        <nav className="eval-label-nav" aria-label="Items">
          {nav.previous !== null ? (
            <Link to={itemHref(setId, nav.previous.itemId)} data-testid="eval-label-prev">
              ← Previous
            </Link>
          ) : (
            <span />
          )}
          {nav.next !== null ? (
            <Link to={itemHref(setId, nav.next.itemId)} data-testid="eval-label-next">
              Next →
            </Link>
          ) : (
            <span />
          )}
        </nav>
      ) : null}
    </section>
  );
}
